import { useEffect, useCallback } from "react";
import { getSocket, joinPortalUserRoom } from "../services/socketService";
import { useAuth } from "../auth/AuthProvider";

/**
 * useNotificationsSocket — turns the socket signals that already exist into an
 * immediate "refetch the inbox" trigger, so a new portal notification does not wait
 * out the header's 30s poll.
 *
 * These three events are the only ones the backend emits alongside a new
 * scope:'portal' notification today:
 *
 *   USER_APPROVED / USER_REJECTED — emitted by emitUserStatusChanged() from the same
 *     request handler that creates the registration_approved/registration_rejected row.
 *   USER_ACCESS_UPDATED — emitted when the user's venue/map grants change, which is
 *     what changes the robots they can see.
 *
 * Deliberately absent, because the backend emits nothing for them:
 *   robot_assigned / robot_removed / advertisement_* rows arrive with no socket
 *   signal at all. The header's periodic unread-count poll is the only thing that
 *   surfaces those, which is why that poll must stay — this hook supplements it, it
 *   does not replace it.
 *
 * Also deliberately absent: 'notification_new'. That event only ever carries
 * scope:'fleet' rows, which have no userId and are broadcast to every subscriber of
 * the unauthenticated 'fleet' room — they are not this user's inbox and must not be
 * merged into it here.
 *
 * The payloads carry no notification content (just { userId }), and the socket
 * transport has no authentication, so nothing received here is rendered directly:
 * the only effect is a refetch of the JWT-scoped REST endpoint, which is the single
 * authoritative source for what this user may see.
 *
 * @param {() => void} onRefresh — refetch callback; must be stable (useCallback).
 */
const REFRESH_EVENTS = ["USER_APPROVED", "USER_REJECTED", "USER_ACCESS_UPDATED"];

const useNotificationsSocket = (onRefresh) => {
  const { user } = useAuth();
  // Sessions restored from localStorage can carry either shape — same defensive
  // read as useAdvertisementSocket.
  const userId = user?.id || user?._id;

  const handleRefresh = useCallback(() => {
    if (typeof onRefresh === "function") onRefresh();
  }, [onRefresh]);

  useEffect(() => {
    if (!userId) return undefined;

    // Idempotent on the backend (a Map keyed by userId), and required because the
    // targeted emits above address a socket id looked up from that map rather than a
    // Socket.IO room — without the join, nothing arrives.
    joinPortalUserRoom(userId);

    const socket = getSocket();
    REFRESH_EVENTS.forEach((event) => socket.on(event, handleRefresh));

    return () => {
      REFRESH_EVENTS.forEach((event) => socket.off(event, handleRefresh));
    };
  }, [userId, handleRefresh]);
};

export default useNotificationsSocket;
